import { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { runInAction } from 'mobx';
import { Box, Grid, Link, Typography } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import CheckIcon from '@material-ui/icons/Check';
import CancelIcon from '@material-ui/icons/Cancel';
import FlagIcon from '@material-ui/icons/Flag';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import { Button, ConfirmDialog, DialogState, FileUploadModal, StandardDialog, usdAmount } from '@roc/ui';
import { useBorrowerBaseballCardStore, useBorrowerTrackRecordStore } from '@roc/feature-borrowers';
import { TRACK_RECORD_CAPITAL_PROVIDER, TRACK_RECORD_REVIEW_TYPE, formatDate } from '@roc/feature-utils';
import { RowAction, RowActionsMenu } from './rowActionsMenu';
import { VerifyRecordDialog } from './verifyRecordDialog';
import { UnverifyRecordDialog } from './unverifyRecordDialog';
import { useCenteredDialogStyles } from './centeredDialogStyles';
import { StatusPill, TrackRecordFilterChips } from './trackRecordTabControls';
import { PropertyAddressLinks } from './propertyAddressLinks';
import { TrackRecordPropertyForm } from './trackRecordPropertyForm';

// Maps a BorrowerTrackRecordVerificationStore.properties row onto this
// tab's design; fields with no real backing are left undefined.
const mapProperty = (property: any) => {
  const address = property.fullAddress || property.purchaseAddress || '—';
  const cityStateZip = property.fullAddress
    ? ''
    : [property.purchaseCity, property.purchaseState, property.purchaseZipcode].filter(Boolean).join(', ');
  return {
    id: property.verifiedTrackRecordsId ?? `${address}-${property.purchaseDate ?? ''}`,
    raw: property,
    address,
    cityStateZip,
    entity: property.entityName || '—',
    dealType: property.dealType || '—',
    purchase: Number(property.purchasePrice) || 0,
    rehab: property.rehabBudget != null && property.rehabBudget !== '' ? Number(property.rehabBudget) : null,
    sale: property.salePrice != null && property.salePrice !== '' ? Number(property.salePrice) : null,
    status: property.isVerified ? 'verified' : 'needsInput',
  };
};

const textOrDash = (value: any) => (value != null && `${value}`.trim() !== '' ? value : '—');
const moneyOrDash = (value: any) => (value != null && value !== '' ? usdAmount({ value }) : '—');

// Legacy expanded-row fields that the overview payload provides.
const getPropertyDetailFields = (details: any): [string, any][] => [
  ['Purchase Date', textOrDash(details.purchaseDate)],
  ['Sale Date', textOrDash(details.saleDate)],
  ['Seller Name', textOrDash(details.purchasedFrom)],
  ['Buyer Name', textOrDash(details.soldTo)],
  ['Rehab Budget', moneyOrDash(details.rehabBudget)],
  ['Property Type', textOrDash(details.propertyType)],
  ['Source Type', textOrDash(details.recordSourceType)],
  ['Verification Source', textOrDash(details.verificationSource)],
  ['Mortgage Amount', moneyOrDash(details.mortgageAmount)],
  ['Lender Name', textOrDash(details.lenderName)],
];

const toEditableProperty = (property: any) => {
  const dealType = `${property.dealType ?? ''}`.toLowerCase();
  const hasDealFlag = !!(property.bridge || property.term || property.groundUp);
  const derived = hasDealFlag
    ? {}
    : dealType.includes('ground')
      ? { groundUp: true, bridge: true, term: true }
      : dealType.includes('bridge') || dealType.includes('flip')
        ? { bridge: true, term: true }
        : dealType.includes('rental') || dealType.includes('term')
          ? { term: true }
          : {};
  return {
    ...property,
    fullAddress: property.fullAddress || property.purchaseAddress || '',
    address2: property.address2 ?? property.purchaseSecondaryAddress ?? '',
    ...derived,
  };
};

// Used by the sidebar nav badges — same verified/needsInput split as this tab.
export const getPropertiesStatusCounts = (verificationStore: any) => {
  const properties = verificationStore?.properties ?? [];
  const verified = properties.filter((property: any) => property.isVerified).length;
  return { verified, needsInput: properties.length - verified };
};

// Same gating trackRecordProperties.tsx uses to decide which row actions show.
const getPropertyActionFlags = (property: any, isInternal: boolean, lockedData: boolean) => {
  const notCapitalProvider = property.recordSourceType && property.recordSourceType !== TRACK_RECORD_CAPITAL_PROVIDER;
  return {
    canEdit: isInternal && !property.isVerified && !property.verificationInProgress,
    canVerify: isInternal && !property.isVerified && !property.verificationInProgress,
    canUnverify: isInternal && property.isVerified && notCapitalProvider && !lockedData,
    canCreateResolution: isInternal && !property.isVerified && !property.verificationInProgress && !property.isVerificationRequested,
    canRemove: !property.isVerified && !property.verificationInProgress && !property.isVerificationRequested,
  };
};

export const PropertiesTab = observer(({
  classes,
  verificationStore,
  isInternal,
}: {
  classes: Record<string, string>;
  verificationStore?: any;
  isInternal?: boolean;
}) => {
  const { borrowerBaseballCardStore } = useBorrowerBaseballCardStore();
  const centeredDialogClasses = useCenteredDialogStyles();
  const { borrowerTrackRecordStore } = useBorrowerTrackRecordStore();
  // Hide Verify/Unverify/Create-resolution outside the internal store where they'd no-op (Remove stays — real method).
  const canManage = !!isInternal && !!borrowerBaseballCardStore?.isTrackRecordVerificationEnabled;
  const [propertiesFilter, setPropertiesFilter] = useState<'all' | 'needsInput' | 'verified'>('all');
  const [expandedProperty, setExpandedProperty] = useState<string | null>(null);

  const [uploadTarget, setUploadTarget] = useState<any | null>(null);
  const [verifyTarget, setVerifyTarget] = useState<any | null>(null);
  const [unverifyTarget, setUnverifyTarget] = useState<any | null>(null);
  const [removeTarget, setRemoveTarget] = useState<any | null>(null);
  const wasEditingRef = useRef(false);

  const properties = (verificationStore?.properties ?? []).filter(Boolean).map(mapProperty);
  const needsInputCount = properties.filter((property) => property.status === 'needsInput').length;
  const verifiedCount = properties.filter((property) => property.status === 'verified').length;
  const visibleProperties = properties.filter((property) =>
    propertiesFilter === 'all' ? true : property.status === propertiesFilter
  );

  // Single overview re-fetch instead of separate property/tier endpoints —
  // keeps properties/entities/licenses/summary all refreshed together.
  const refreshProperties = () => {
    verificationStore?.loadFromOverview?.();
  };

  const taskBodyFor = (property: any) => ({
    sectionProcess: 'TRACK_RECORD_REVIEW',
    objectType: 'PROPERTY_TRACK_RECORD',
    taskTemplateType: 'PROPERTY_TRACK_RECORD',
    objectId: property.verifiedTrackRecordsId,
  });

  // Docs for the expanded row; currentTasks is refreshed by getTasks and by createTaskWithFile.
  const uploadedDocs = (verificationStore?.currentTasks ?? []).filter((task: any) => task?.originalFileName);

  const toggleExpand = (property: any) => {
    const expanding = expandedProperty !== property.id;
    setExpandedProperty(expanding ? property.id : null);
    if (expanding && property.raw.verifiedTrackRecordsId) verificationStore?.getTasks(taskBodyFor(property.raw));
  };

  const handleUpload = async (files: any[]) => {
    const property = uploadTarget;
    setUploadTarget(null);
    if (!files?.[0] || !property) return;
    const uploaded = await verificationStore?.createTaskWithFile(taskBodyFor(property), files[0]);
    if (uploaded?.fileDownloadId) {
      setExpandedProperty(mapProperty(property).id);
      verificationStore?.globalStore?.notificationStore?.showSuccessNotification({ message: 'Document uploaded successfully' });
    }
  };

  const handleVerify = async (verificationSource: string, othersReason: string) => {
    const property = verifyTarget;
    setVerifyTarget(null);
    if (!property) return;
    const body = {
      ...property,
      purchaseDate: formatDate(property.purchaseDate, 'MM/dd/yyyy'),
      saleDate: formatDate(property.saleDate, 'MM/dd/yyyy'),
    };
    await borrowerBaseballCardStore?.verifyTrackRecord(body, borrowerBaseballCardStore?.loanId, verificationSource, othersReason);
    refreshProperties();
  };

  const handleUnverify = async (reason: string) => {
    const property = unverifyTarget;
    setUnverifyTarget(null);
    if (!property) return;
    await borrowerBaseballCardStore?.unverifyTrackRecord(property.verifiedTrackRecordsId, borrowerBaseballCardStore?.loanId, reason);
    refreshProperties();
  };

  const handleCreateResolution = async (property: any) => {
    // The backend requires both ids; in the loan-task flow loanId only comes from the task document.
    const borrowerId = verificationStore?.borrowerInfo?.borrowerId || borrowerBaseballCardStore?.currentBorrower;
    const loanId = borrowerBaseballCardStore?.loanId || borrowerTrackRecordStore?.document?.loanId;
    if (!borrowerId || !loanId) {
      verificationStore?.globalStore?.notificationStore?.showErrorNotification({ message: 'Unable to request verification: missing borrower or loan information.' });
      return;
    }
    runInAction(() => {
      property.purchaseDate = formatDate(property.purchaseDate, 'MM/dd/yyyy');
      property.saleDate = formatDate(property.saleDate, 'MM/dd/yyyy');
      property.isVerificationRequested = true;
    });
    if (property.verifiedTrackRecordsId == null) {
      const response: any = await borrowerBaseballCardStore?.savePropertyInformation(property);
      runInAction(() => {
        property.verifiedTrackRecordsId = response?.data?.verifiedTrackRecordsId;
      });
    }
    const trackRecords = [];
    const resolutions = [];
    for (const tr of verificationStore?.properties || []) {
      if (tr.isVerificationRequested === true) {
        const fullAddress = tr.fullAddress || `${tr.purchaseAddress}, ${tr.purchaseCity}, ${tr.purchaseState}, ${tr.purchaseZipcode}`;
        trackRecords.push(tr);
        resolutions.push({
          itemSection: `${TRACK_RECORD_REVIEW_TYPE} ${tr.verifiedTrackRecordsId}`,
          itemName: fullAddress.trim(),
          itemDescription: `Address: ${fullAddress}`,
        });
      }
    }
    const request = { borrowerId, letterOfExplanation: resolutions, trackRecords };
    await borrowerBaseballCardStore?.requestTrackRecordVerification(request, borrowerId, loanId, TRACK_RECORD_REVIEW_TYPE);
    refreshProperties();
  };

  // Edit reuses the legacy property form dialog; handleEditExistingTrackRecordProperty
  // builds currentDocumentsToUpload from currentTasks, so tasks must be loaded first.
  const handleEdit = async (property: any) => {
    if (!verificationStore) return;
    wasEditingRef.current = true;
    if (borrowerBaseballCardStore) {
      borrowerBaseballCardStore.propertyInformationDialogState = DialogState.EDIT;
    }
    verificationStore.trackRecordPropertyFormStore.form.fields.screenType.value = 'addProperty';
    verificationStore.currentTasks = [];
    verificationStore.currentProperty = property;
    if (property.verifiedTrackRecordsId) await verificationStore.getTasks(taskBodyFor(property));
    const index = (verificationStore.properties ?? []).indexOf(property);
    verificationStore.handleEditExistingTrackRecordProperty(toEditableProperty(property), index);
  };

  const handleCloseEdit = async () => {
    await borrowerBaseballCardStore?.setPropagateAddress?.(false);
    verificationStore?.setPropertyFormActive(false);
    verificationStore?.trackRecordPropertyFormStore?.reset();
    borrowerBaseballCardStore?.propertyFormStore?.resetForm?.();
  };

  // The edit save (or cancel) closes the dialog from the store; refresh the overview once it closes.
  const isFormActive = !!verificationStore?.isPropertyFormActive;
  useEffect(() => {
    if (!isFormActive && wasEditingRef.current) {
      wasEditingRef.current = false;
      refreshProperties();
    }
  }, [isFormActive]);

  const handleRemove = () => {
    const property = removeTarget;
    setRemoveTarget(null);
    if (!property) return;
    verificationStore?.handleRemoveTrackRecordProperty?.(property);
    refreshProperties();
  };

  return (
    <>
      <Box className={classes.listHeaderRow}>
        <Box>
          <Typography variant="h5" className={classes.overviewTitle}>
            Properties
          </Typography>
          <Typography className={classes.overviewSubtitle} style={{ marginBottom: 0 }}>
            {needsInputCount} of {properties.length} properties need a document before they can be verified.
          </Typography>
        </Box>
      </Box>

      <TrackRecordFilterChips
        classes={classes}
        selected={propertiesFilter}
        onSelect={setPropertiesFilter}
        total={properties.length}
        needsInputCount={needsInputCount}
        verifiedCount={verifiedCount}
      />

      <Box className={classes.table}>
        <Box className={`${classes.tableRow} ${classes.tableHeaderRow}`}>
          <Typography className={classes.tableHeaderCell}>Property</Typography>
          <Typography className={classes.tableHeaderCell}>Entity</Typography>
          <Typography className={classes.tableHeaderCell}>Deal Type</Typography>
          <Typography className={classes.tableHeaderCell}>Purchase</Typography>
          <Typography className={classes.tableHeaderCell}>Rehab</Typography>
          <Typography className={classes.tableHeaderCell}>Sale</Typography>
          <Typography className={classes.tableHeaderCell}>Status</Typography>
          <span />
          <span />
        </Box>
        {visibleProperties.map((property) => {
          const isExpanded = expandedProperty === property.id;
          const flags = getPropertyActionFlags(property.raw, canManage, !!borrowerBaseballCardStore?.baseballCardDetails?.lockedData);
          const actions: RowAction[] = [
            ...(flags.canEdit ? [{ key: 'edit', label: 'Edit', icon: EditIcon, onClick: () => handleEdit(property.raw) }] : []),
            { key: 'upload', label: 'Upload Document', icon: CloudUploadIcon, onClick: () => setUploadTarget(property.raw) },
            ...(flags.canVerify ? [{ key: 'verify', label: 'Verify Property', icon: CheckIcon, onClick: () => setVerifyTarget(property.raw) }] : []),
            ...(flags.canUnverify ? [{ key: 'unverify', label: 'Unverify Property', icon: CancelIcon, onClick: () => setUnverifyTarget(property.raw) }] : []),
            ...(flags.canCreateResolution ? [{ key: 'createResolution', label: 'Create Resolution', icon: FlagIcon, onClick: () => handleCreateResolution(property.raw) }] : []),
            ...(flags.canRemove ? [{ key: 'remove', label: 'Remove', icon: DeleteIcon, onClick: () => setRemoveTarget(property.raw) }] : []),
          ];
          return (
            <Box
              key={property.id}
              className={`${classes.tableRowContainer} ${
                property.status === 'needsInput' ? classes.tableRowNeedsInput : ''
              }`}
            >
              <Box
                className={classes.tableRow}
                style={{ borderBottom: 'none', cursor: 'pointer' }}
                onClick={() => toggleExpand(property)}
              >
                <Box>
                  <Typography className={classes.tableCellPrimary}>{property.address}</Typography>
                  <Typography className={classes.tableCellSecondary}>{property.cityStateZip}</Typography>
                </Box>
                <Typography className={classes.tableCell}>{property.entity}</Typography>
                <Typography className={classes.tableCell}>{property.dealType}</Typography>
                <Typography className={classes.tableCell}>{usdAmount({ value: property.purchase })}</Typography>
                <Typography className={classes.tableCell}>
                  {property.rehab != null ? usdAmount({ value: property.rehab }) : '—'}
                </Typography>
                <Typography className={classes.tableCell}>
                  {property.sale != null ? usdAmount({ value: property.sale }) : '—'}
                </Typography>
                <StatusPill classes={classes} status={property.status} />
                <RowActionsMenu actions={actions} />
                <ExpandMoreIcon
                  className={classes.expandChevron}
                  style={{ transform: isExpanded ? 'rotate(180deg)' : 'none' }}
                />
              </Box>
              {isExpanded && (
                <Box className={classes.entityExpandContent}>
                  <Box className={classes.propertyDetailGrid}>
                    {getPropertyDetailFields(property.raw).map(([label, value]) => (
                      <Box key={label}>
                        <Typography className={classes.entityDetailLabel}>{label}</Typography>
                        <Typography className={classes.entityDetailValue}>{value}</Typography>
                      </Box>
                    ))}
                  </Box>

                  <Box className={classes.propertyDetailGrid}>
                    {isInternal && (
                      <PropertyAddressLinks
                        classes={classes}
                        property={property.raw}
                        borrowerBaseballCardStore={borrowerBaseballCardStore}
                      />
                    )}
                    <Box>
                      <Typography className={classes.entityDetailLabel}>Documents</Typography>
                      <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        {uploadedDocs.length > 0
                          ? uploadedDocs.map((task) => (
                            <Link
                              key={task.taskId}
                              component="button"
                              onClick={() => verificationStore?.downloadTaskFile(task.taskId, task.originalFileName, task.fileExtension)}
                            >
                              {task.originalFileName}
                            </Link>
                          ))
                          : <Typography className={classes.entityDetailValue}>No documents uploaded</Typography>}
                      </Box>
                    </Box>
                  </Box>

                  <Typography className={classes.entityDetailLabel}>Comment</Typography>
                  <Typography className={classes.entityDetailValue} style={{ marginBottom: 16 }}>
                    {textOrDash(property.raw.comment)}
                  </Typography>

                  {property.whyAttention && (
                    <>
                      <Typography className={classes.entityAttentionTitle}>Why this needs attention</Typography>
                      <Typography className={classes.entityAttentionText}>{property.whyAttention}</Typography>

                      <Typography className={classes.entityDocsTitle}>Helpful documents</Typography>
                      <Typography className={classes.entityDocsText}>{property.helpfulDocuments}</Typography>

                      <Box className={classes.entityActionsRow}>
                        <span className={classes.entityNoDocumentLink}>I don't have this document</span>
                        <button type="button" className={classes.uploadSupportingDocButton}>
                          Upload Supporting Document
                        </button>
                      </Box>
                    </>
                  )}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      <StandardDialog
        open={isFormActive}
        handleClose={handleCloseEdit}
        maxWidth="lg"
        fullWidth
        removeContentBox
        PaperProps={{ style: { maxHeight: '85vh', overflowY: 'auto' } }}
        dialogContent={
          <Grid item xs={12}>
            <TrackRecordPropertyForm
              store={verificationStore}
              handleClose={handleCloseEdit}
              borrowerBaseballCardStore={borrowerBaseballCardStore}
            />
          </Grid>
        }
      />

      <FileUploadModal
        dialogClasses={centeredDialogClasses}
        open={uploadTarget != null}
        title="Upload Document"
        onClose={() => setUploadTarget(null)}
        onUpload={handleUpload}
      />

      <VerifyRecordDialog
        open={verifyTarget != null}
        title="Verify Property"
        recordLabel="property"
        sourceOptions={['RPR', 'Roc Title History', 'HUD', 'Other']}
        onClose={() => setVerifyTarget(null)}
        onConfirm={handleVerify}
      />

      <UnverifyRecordDialog
        open={unverifyTarget != null}
        title="Unverify Property"
        recordLabel="property"
        onClose={() => setUnverifyTarget(null)}
        onConfirm={handleUnverify}
      />

      <ConfirmDialog
        open={removeTarget != null}
        title="Remove Track Record"
        confirmButtonText="Yes"
        body="Are you sure you want to remove the record?"
        handleClose={() => setRemoveTarget(null)}
        handleCancel={() => setRemoveTarget(null)}
        handleConfirm={handleRemove}
      />
    </>
  );
});
